import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { MidiMessage } from '@/lib/midiUtils'

const MAX_LOG_SIZE = 200

export interface MidiDevice {
  id: string
  name: string
  manufacturer: string
  state: 'connected' | 'disconnected'
}

/** A single point-to-point route from one MIDI input to one MIDI output. */
export interface HwMidiRoute {
  inputId: string
  outputId: string
}

/**
 * Synthetic input id used for the on-screen virtual keyboard so it can
 * participate in the same routing fan-out as a hardware input.
 */
export const VIRTUAL_KEYBOARD_INPUT_ID = 'virtual-keyboard'

export const VIRTUAL_KEYBOARD_INPUT: MidiDevice = {
  id: VIRTUAL_KEYBOARD_INPUT_ID,
  name: 'Virtual Keyboard',
  manufacturer: 'Busswerks',
  state: 'connected',
}

/**
 * Synthetic id for the browser sequencer. The sequencer is *both*
 * a virtual input (its pads emit MIDI to the router's output fan-out)
 * AND a virtual destination (cabling a hardware input to it fires
 * pads by note number). MidiRouter renders it on both sides of the
 * patchbay; the trigger handler in `useWebMidi` routes note-on
 * events into the sequencer callback registered at runtime.
 */
export const VIRTUAL_SEQUENCER_INPUT_ID = 'virtual-sequencer'

export const VIRTUAL_SEQUENCER_INPUT: MidiDevice = {
  id: VIRTUAL_SEQUENCER_INPUT_ID,
  name: 'Sequencer',
  manufacturer: 'Busswerks',
  state: 'connected',
}

/**
 * Virtual MIDI *sources* — rendered as input rows in the router.
 * Adding a new app-level MIDI source is a matter of defining its
 * ID + device constant and appending here.
 */
export const VIRTUAL_INPUTS: readonly MidiDevice[] = [
  VIRTUAL_KEYBOARD_INPUT,
  VIRTUAL_SEQUENCER_INPUT,
]

/**
 * Virtual MIDI *destinations* — rendered as output rows in the
 * router. Cabling a hardware input → here invokes the registered
 * in-app handler instead of sending to `access.outputs.get(id)`.
 * Only the Sequencer participates today.
 */
export const VIRTUAL_DESTINATIONS: readonly MidiDevice[] = [
  VIRTUAL_SEQUENCER_INPUT,
]

const VIRTUAL_INPUT_IDS: ReadonlySet<string> = new Set(
  VIRTUAL_INPUTS.map(v => v.id),
)

const VIRTUAL_DESTINATION_IDS: ReadonlySet<string> = new Set(
  VIRTUAL_DESTINATIONS.map(v => v.id),
)

/** True when `id` belongs to any in-app virtual MIDI source. */
export function isVirtualInput(id: string): boolean {
  return VIRTUAL_INPUT_IDS.has(id)
}

/** True when `id` belongs to any in-app virtual MIDI destination. */
export function isVirtualDestination(id: string): boolean {
  return VIRTUAL_DESTINATION_IDS.has(id)
}

interface MidiStoreState {
  supported: boolean
  hwInputs: MidiDevice[]
  hwOutputs: MidiDevice[]
  routes: HwMidiRoute[]
  messageLog: MidiMessage[]
  midiPanelOpen: boolean
  selectedHwInputId: string | null
  /**
   * When true, incoming MIDI from every enabled hardware input is
   * broadcast through the global mapping dispatcher regardless of
   * whether the user is on the Sequencer page. Users toggle this
   * from the MIDI-In button popover in Transport. Off by default
   * so mapping behavior is explicit: the user opts in to "MIDI
   * controls anything, from anywhere."
   *
   * Note the distinction from the sequencer's `midiInEnabled`
   * flag: that one gates the *sequencer-specific* fan-out (pads
   * triggered by incoming notes). Forwarding adds the global
   * mapping dispatch on top and is independent of sequencer mode.
   */
  midiForwardingEnabled: boolean
  /**
   * Per-output latency compensation in milliseconds. A positive
   * value means MIDI note-on is scheduled that many ms EARLIER
   * than the matching audio trigger — so the external device's
   * MIDI-in + sound-generation delay lines up with the in-app
   * synth. Defaults to 0 per output (no compensation).
   *
   * Dial in per device: tap a pad that plays both a synth voice
   * and sends MIDI to the hardware; if MIDI arrives late, bump
   * the output's latency up. Typical hardware synths need
   * 5–30 ms; slow USB-MIDI interfaces can need more.
   */
  outputLatencyMs: Record<string, number>
  /**
   * Per-sequencer-pad override for the incoming MIDI note that
   * triggers that pad. Keyed by pad index (0..PADS_PER_PROJECT-1).
   * Set via MIDI Learn: user focuses a pad, plays a key on their
   * controller, the resulting note number is stored here.
   *
   * Why this is global (not per-project): hardware controllers have
   * a fixed pad → note layout that doesn't change with the musical
   * context. Learning once on your SP-404 should work across every
   * project. If two projects need different layouts the user can
   * re-learn; in practice they won't.
   *
   * When a pad has a binding, the matcher uses it instead of the
   * chromatic default for that pad. Unbound pads fall back to the
   * chromatic-by-pad default: pad i is triggered by MIDI note
   * `48 + i` (C3 through D#4 across the 16 slots). Drum-keyboard
   * users on a GM layout remap via MIDI Learn once per controller.
   */
  padNoteBindings: Record<number, number>

  setSupported: (v: boolean) => void
  setHwDevices: (inputs: MidiDevice[], outputs: MidiDevice[]) => void
  addRoute: (inputId: string, outputId: string) => void
  removeRoute: (inputId: string, outputId: string) => void
  toggleRoute: (inputId: string, outputId: string) => void
  clearRoutesForInput: (inputId: string) => void
  clearAllRoutes: () => void
  pushMessage: (msg: MidiMessage) => void
  clearLog: () => void
  toggleMidiPanel: () => void
  setSelectedHwInput: (id: string | null) => void
  setOutputLatency: (outputId: string, latencyMs: number) => void
  bindPadNote: (padIndex: number, note: number) => void
  clearPadNote: (padIndex: number) => void
  clearAllPadBindings: () => void
  setMidiForwardingEnabled: (v: boolean) => void
}

function routeExists(
  routes: HwMidiRoute[],
  inputId: string,
  outputId: string,
): boolean {
  return routes.some(r => r.inputId === inputId && r.outputId === outputId)
}

export const useMidiStore = create<MidiStoreState>()(
  persist(
    set => ({
      supported:
        typeof navigator !== 'undefined' && 'requestMIDIAccess' in navigator,
      hwInputs: [],
      hwOutputs: [],
      routes: [],
      messageLog: [],
      midiPanelOpen: false,
      selectedHwInputId: null,
      outputLatencyMs: {},
      padNoteBindings: {},
      midiForwardingEnabled: false,

      setSupported: v => set({ supported: v }),

      setMidiForwardingEnabled: v => set({ midiForwardingEnabled: v }),

      bindPadNote: (padIndex, note) =>
        set(state => {
          const padIdx = Math.trunc(padIndex)
          const n = Math.max(0, Math.min(127, Math.round(note)))
          if (!Number.isFinite(padIdx) || padIdx < 0) return state
          // If this note is already bound to a *different* pad, steal
          // the binding — controllers reuse notes across banks, so
          // the user's intent is almost always "rebind to the new
          // pad". Leaving the stale binding would silently continue
          // triggering the old pad.
          const next: Record<number, number> = {}
          for (const [idx, existing] of Object.entries(state.padNoteBindings)) {
            if (existing !== n) next[Number(idx)] = existing
          }
          next[padIdx] = n
          return { padNoteBindings: next }
        }),

      clearPadNote: padIndex =>
        set(state => {
          const padIdx = Math.trunc(padIndex)
          if (state.padNoteBindings[padIdx] === undefined) return state
          const next = { ...state.padNoteBindings }
          delete next[padIdx]
          return { padNoteBindings: next }
        }),

      clearAllPadBindings: () => set({ padNoteBindings: {} }),

      setOutputLatency: (outputId, latencyMs) =>
        set(state => {
          // Clamp to [0, 500ms]. Negative values aren't meaningful
          // (we can't fire MIDI after the audio has already
          // started); 500ms is well beyond any reasonable device
          // latency and guards against accidental giant values.
          const clamped = Math.max(0, Math.min(500, Math.round(latencyMs)))
          const next = { ...state.outputLatencyMs }
          if (clamped === 0) delete next[outputId]
          else next[outputId] = clamped
          return { outputLatencyMs: next }
        }),

      setHwDevices: (inputs, outputs) =>
        set({ hwInputs: inputs, hwOutputs: outputs }),

      addRoute: (inputId, outputId) =>
        set(state =>
          routeExists(state.routes, inputId, outputId)
            ? state
            : { routes: [...state.routes, { inputId, outputId }] },
        ),

      removeRoute: (inputId, outputId) =>
        set(state => ({
          routes: state.routes.filter(
            r => !(r.inputId === inputId && r.outputId === outputId),
          ),
        })),

      toggleRoute: (inputId, outputId) =>
        set(state =>
          routeExists(state.routes, inputId, outputId)
            ? {
                routes: state.routes.filter(
                  r => !(r.inputId === inputId && r.outputId === outputId),
                ),
              }
            : { routes: [...state.routes, { inputId, outputId }] },
        ),

      clearRoutesForInput: inputId =>
        set(state => ({
          routes: state.routes.filter(r => r.inputId !== inputId),
        })),

      clearAllRoutes: () => set({ routes: [] }),

      pushMessage: msg =>
        set(state => {
          const log =
            state.messageLog.length >= MAX_LOG_SIZE
              ? [...state.messageLog.slice(-MAX_LOG_SIZE + 1), msg]
              : [...state.messageLog, msg]
          return { messageLog: log }
        }),

      clearLog: () => set({ messageLog: [] }),

      toggleMidiPanel: () =>
        set(state => ({ midiPanelOpen: !state.midiPanelOpen })),

      setSelectedHwInput: id => set({ selectedHwInputId: id }),
    }),
    {
      name: 'busswerks-midi-router',
      // Only persist the user's routing choices and the input they had
      // focused for the monitor. Device lists, the message log, modal
      // state, and Web MIDI support flag are all derived at runtime.
      partialize: state => ({
        routes: state.routes,
        selectedHwInputId: state.selectedHwInputId,
        outputLatencyMs: state.outputLatencyMs,
        padNoteBindings: state.padNoteBindings,
        midiForwardingEnabled: state.midiForwardingEnabled,
      }),
      storage: createJSONStorage(() =>
        typeof window === 'undefined' ? memoryStorage() : window.localStorage,
      ),
    },
  ),
)

/**
 * Stub Storage used during SSR / non-browser test runs. The persist
 * middleware requires a Storage implementation but never reads/writes
 * meaningfully outside the browser.
 */
function memoryStorage(): Storage {
  const store = new Map<string, string>()
  return {
    get length() {
      return store.size
    },
    clear: () => store.clear(),
    getItem: key => store.get(key) ?? null,
    key: index => Array.from(store.keys())[index] ?? null,
    removeItem: key => {
      store.delete(key)
    },
    setItem: (key, value) => {
      store.set(key, value)
    },
  }
}
